import React, { useState, useEffect, useCallback } from 'react';
import { getAuditLog, getAuditSummary } from '../api';
import { useTheme } from '../hooks/useTheme';

const ACTION_META = {
  login:         { icon: '🔑', label: 'Login',         bg: '#f0fdf4', fg: '#16a34a' },
  view:          { icon: '👁',  label: 'View',          bg: '#f1f5f9', fg: '#64748b' },
  create:        { icon: '➕', label: 'Create',         bg: '#eff6ff', fg: '#2563eb' },
  update:        { icon: '✏️', label: 'Update',         bg: '#fffbeb', fg: '#d97706' },
  delete:        { icon: '🗑', label: 'Delete',         bg: '#fef2f2', fg: '#dc2626' },
  status_change: { icon: '🔄', label: 'Status Change', bg: '#f3e8ff', fg: '#7c3aed' },
  export:        { icon: '📤', label: 'Export',         bg: '#ecfdf5', fg: '#059669' },
};

const ENTITY_LABELS = {
  project: 'Project', task: 'Task', billing_period: 'Billing Period',
  user: 'User', sov_line: 'SOV Line', change_order: 'Change Order',
  forecast: 'Forecast',
};

const fmtTimestamp = (d) => {
  if (!d) return '–';
  try {
    const dt = new Date(d + 'Z'); // SQLite stores UTC
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
      dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return d; }
};

const fmtDay = (d) => {
  if (!d) return '–';
  try { return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
  catch { return d; }
};

const parseDetails = (d) => {
  if (!d) return null;
  try { return JSON.parse(d); } catch { return d; }
};

const AuditLogPage = ({ onOpenProject }) => {
  const t = useTheme();

  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('timeline'); // timeline | summary

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Dynamic color constants that respond to theme
  const C = {
    heading: t.heading, text: t.text, textSecondary: t.textSecondary, textMuted: t.textMuted,
    cardBorder: t.cardBorder, bgAlt: t.bgAlt, bg: t.bg,
    blue600:'#2563eb', blue50:'#eff6ff',
    green600:'#16a34a', green50:'#f0fdf4',
    amber600:'#d97706', amber50:'#fffbeb',
    rose600:'#dc2626', rose50:'#fef2f2',
    indigo:'#6366f1',
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterAction) params.action = filterAction;
      if (filterEntity) params.entity_type = filterEntity;
      if (filterSearch) params.search = filterSearch;
      params.limit = 200;

      const [logData, summaryData] = await Promise.all([
        getAuditLog(params),
        getAuditSummary(),
      ]);
      setLogs(logData);
      setSummary(summaryData);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterAction, filterEntity, filterSearch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilterSearch(searchInput);
  };

  const card = { background: C.bg, borderRadius: 16, border: `1px solid ${C.cardBorder}`, padding: '24px 28px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,.03)' };
  const pill = (active) => ({
    padding: '8px 20px', borderRadius: 24, border: 'none',
    background: active ? C.indigo : 'transparent', color: active ? '#fff' : C.textSecondary,
    fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all .15s',
    boxShadow: active ? '0 2px 10px rgba(99,102,241,.3)' : 'none',
  });

  if (loading && !summary) return (
    <div style={{ padding: '80px 20px', textAlign: 'center' }}>
      <div style={{ width: 44, height: 44, border: `3px solid ${C.bgAlt}`, borderTopColor: C.indigo, borderRadius: '50%', margin: '0 auto 16px', animation: 'spin .7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ color: C.textSecondary, fontSize: 14, fontWeight: 500 }}>Loading audit log...</div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: C.heading, letterSpacing: '-.4px' }}>Audit Log</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.textMuted }}>
            Every login, view, and change across the system — fully auditable.
          </p>
        </div>
        <div style={{ fontSize: 12, color: C.textMuted, fontWeight: 500, padding: '6px 14px', borderRadius: 8, backgroundColor: C.bgAlt }}>
          {summary?.totalEvents || 0} total events
        </div>
      </div>

      {/* Summary KPIs */}
      {summary && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {summary.byAction.map(a => {
            const meta = ACTION_META[a.action] || { icon: '📝', label: a.action, bg: C.bgAlt, fg: C.textSecondary };
            const isActive = filterAction === a.action;
            return (
              <div key={a.action}
                onClick={() => setFilterAction(isActive ? '' : a.action)}
                style={{
                  padding: '14px 20px', borderRadius: 12, border: `1px solid ${isActive ? meta.fg : C.cardBorder}`,
                  backgroundColor: isActive ? meta.bg : C.bg, cursor: 'pointer', transition: 'all .15s', minWidth: 100,
                }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: C.textMuted, marginBottom: 4 }}>
                  {meta.icon} {meta.label}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: meta.fg }}>{a.count}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters Bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
        padding: '12px 20px', borderRadius: 12, border: `1px solid ${C.cardBorder}`, backgroundColor: C.bg,
      }}>
        <div style={{ display: 'flex', gap: 4, background: C.bgAlt, borderRadius: 28, padding: 4 }}>
          <button style={pill(view === 'timeline')} onClick={() => setView('timeline')}>Timeline</button>
          <button style={pill(view === 'summary')} onClick={() => setView('summary')}>Dashboard</button>
        </div>

        <div style={{ width: 1, height: 28, backgroundColor: C.cardBorder }} />

        <select value={filterEntity} onChange={e => setFilterEntity(e.target.value)} style={{
          padding: '8px 12px', fontSize: 13, fontWeight: 600, border: `1px solid ${C.cardBorder}`,
          borderRadius: 8, backgroundColor: C.bg, color: C.heading, cursor: 'pointer', outline: 'none',
        }}>
          <option value="">All Types</option>
          {Object.entries(ENTITY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, flex: 1 }}>
          <input
            type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
            placeholder="Search names, users, details..."
            style={{
              flex: 1, padding: '8px 12px', fontSize: 13, border: `1px solid ${C.cardBorder}`,
              borderRadius: 8, outline: 'none', color: C.heading,
            }}
          />
          <button type="submit" style={{
            padding: '8px 16px', borderRadius: 8, border: 'none', backgroundColor: C.indigo,
            color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>Search</button>
          {(filterAction || filterEntity || filterSearch) && (
            <button type="button" onClick={() => { setFilterAction(''); setFilterEntity(''); setFilterSearch(''); setSearchInput(''); }} style={{
              padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.cardBorder}`, backgroundColor: C.bg,
              color: C.rose600, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>Clear</button>
          )}
        </form>
      </div>

      {/* ═══ TIMELINE VIEW ═══ */}
      {view === 'timeline' && (
        <div style={card}>
          {logs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: C.textMuted }}>
              <div style={{ fontSize: 36, marginBottom: 12, opacity: .5 }}>📋</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.textSecondary }}>No Events Found</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                {filterAction || filterEntity || filterSearch ? 'Try adjusting your filters.' : 'Events will appear here as people use the system.'}
              </div>
            </div>
          ) : (
            <div style={{ position: 'relative', paddingLeft: 24 }}>
              <div style={{ position: 'absolute', left: 8, top: 4, bottom: 4, width: 2, backgroundColor: C.cardBorder }} />

              {logs.map((log, idx) => {
                const meta = ACTION_META[log.action] || { icon: '📝', label: log.action, bg: C.bgAlt, fg: C.textSecondary };
                const details = parseDetails(log.details);
                return (
                  <div key={log.id} style={{ position: 'relative', paddingBottom: idx < logs.length - 1 ? 14 : 0, paddingLeft: 20 }}>
                    {/* Dot */}
                    <div style={{
                      position: 'absolute', left: -20, top: 4, width: 14, height: 14,
                      borderRadius: '50%', backgroundColor: meta.bg, border: `2px solid ${meta.fg}`,
                    }} />

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                          {/* Action badge */}
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                            backgroundColor: meta.bg, color: meta.fg, textTransform: 'uppercase', letterSpacing: .3,
                          }}>{meta.icon} {meta.label}</span>

                          {/* Entity type */}
                          {log.entity_type && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: C.textSecondary }}>
                              {ENTITY_LABELS[log.entity_type] || log.entity_type}
                            </span>
                          )}

                          {/* Entity name */}
                          {log.entity_name && (
                            <span style={{ fontSize: 13, fontWeight: 600, color: C.heading }}>
                              {log.entity_name}
                            </span>
                          )}

                          {/* Project link */}
                          {log.project_id && log.entity_type !== 'project' && (
                            <button onClick={() => onOpenProject && onOpenProject(log.project_id)} style={{
                              fontSize: 10, fontWeight: 600, color: C.blue600, background: C.blue50,
                              border: 'none', borderRadius: 4, padding: '1px 6px', cursor: 'pointer',
                            }}>Project #{log.project_id}</button>
                          )}
                        </div>

                        {/* User + details */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.textSecondary }}>
                          {log.user_name && (
                            <span style={{ fontWeight: 600 }}>{log.user_name}</span>
                          )}
                          {details && typeof details === 'object' ? (
                            <span>
                              {Object.entries(details).map(([k, v]) => (
                                <span key={k} style={{ marginRight: 8 }}>
                                  <span style={{ color: C.textMuted }}>{k}:</span> {String(v)}
                                </span>
                              ))}
                            </span>
                          ) : details ? (
                            <span style={{ color: C.textMuted }}>{String(details)}</span>
                          ) : null}
                        </div>
                      </div>

                      <div style={{ fontSize: 11, color: C.textMuted, whiteSpace: 'nowrap', marginTop: 2 }}>
                        {fmtTimestamp(log.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ DASHBOARD VIEW ═══ */}
      {view === 'summary' && summary && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Activity by Day */}
          <div style={card}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.heading }}>Daily Activity (Last 30 Days)</h3>
            {summary.byDay.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px', color: C.textMuted, fontSize: 13 }}>No activity yet.</div>
            ) : (
              <div>
                {summary.byDay.map((d, idx) => {
                  const maxCount = Math.max(...summary.byDay.map(x => x.count));
                  const pct = maxCount > 0 ? (d.count / maxCount) * 100 : 0;
                  return (
                    <div key={d.day} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0',
                      borderBottom: idx < summary.byDay.length - 1 ? `1px solid ${C.bgAlt}` : 'none',
                    }}>
                      <div style={{ fontSize: 12, color: C.textSecondary, minWidth: 90, fontWeight: 500 }}>{fmtDay(d.day)}</div>
                      <div style={{ flex: 1, height: 18, backgroundColor: C.bgAlt, borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, backgroundColor: C.indigo, borderRadius: 4, transition: 'width .3s' }} />
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.text, minWidth: 30, textAlign: 'right' }}>{d.count}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Top Users */}
          <div style={card}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.heading }}>Most Active Users</h3>
            {summary.byUser.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px', color: C.textMuted, fontSize: 13 }}>No user activity yet.</div>
            ) : (
              <div>
                {summary.byUser.map((u, idx) => (
                  <div key={u.user_id || idx} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0',
                    borderBottom: idx < summary.byUser.length - 1 ? `1px solid ${C.bgAlt}` : 'none',
                  }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%', backgroundColor: C.blue50,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700, color: C.blue600, flexShrink: 0,
                    }}>
                      {(u.user_name || '?').split(' ').map(n => n[0]).join('')}
                    </div>
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.heading }}>{u.user_name || 'Unknown'}</div>
                    <div style={{
                      fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 8,
                      backgroundColor: `${C.indigo}15`, color: C.indigo,
                    }}>{u.count} events</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* By Entity Type */}
          <div style={card}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.heading }}>Activity by Type</h3>
            {summary.byEntityType.map((e, idx) => (
              <div key={e.entity_type} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: idx < summary.byEntityType.length - 1 ? `1px solid ${C.bgAlt}` : 'none',
              }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                  {ENTITY_LABELS[e.entity_type] || e.entity_type}
                </span>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.heading }}>{e.count}</span>
              </div>
            ))}
          </div>

          {/* Recent Logins */}
          <div style={card}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.heading }}>Recent Logins</h3>
            {summary.recentLogins.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px', color: C.textMuted, fontSize: 13 }}>No logins recorded yet.</div>
            ) : (
              summary.recentLogins.map((l, idx) => (
                <div key={idx} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 0', borderBottom: idx < summary.recentLogins.length - 1 ? `1px solid ${C.bgAlt}` : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 14 }}>🔑</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.heading }}>{l.user_name}</span>
                  </div>
                  <span style={{ fontSize: 11, color: C.textMuted }}>{fmtTimestamp(l.created_at)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
